/**
 * BulkClientOperationsCard
 * =====================================================================
 * Admin → Tools & Security card surfacing two bulk client operations:
 *
 *   1. **Bulk Set Gender** — find clients with missing / 'U' gender
 *      and fix them in one round-trip. Eligibility 270s fail with
 *      999 IK4*3*1068*7*U when gender is blank, so this directly
 *      stops paying for rejected eligibility checks.
 *
 *   2. **Bulk Apply Label by Rule** — pick a "find clients that
 *      match X" rule (missing email, missing phone, etc.), preview
 *      the matches, then apply an existing ClientLabel to the lot.
 *      Idempotent — clients already carrying the label are skipped.
 *
 * Backend contract: /api/admin/clients/{missing-gender,bulk-set-gender,
 *                                      rules,rule-preview/{id},
 *                                      bulk-apply-label}
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Users, Loader2, Tag, UserCog } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BulkClientOperationsCard = () => {
  // ── Bulk Set Gender state ──
  const [genderDialogOpen, setGenderDialogOpen] = useState(false);
  const [genderLoading, setGenderLoading] = useState(false);
  const [genderApplying, setGenderApplying] = useState(false);
  const [genderClients, setGenderClients] = useState([]);
  const [genderEdits, setGenderEdits] = useState({}); // {client_id: "M" | "F"}
  const [genderSearch, setGenderSearch] = useState('');

  // ── Bulk Apply Label state ──
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [labels, setLabels] = useState([]);
  const [selectedRule, setSelectedRule] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewClients, setPreviewClients] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [applying, setApplying] = useState(false);

  // ── Load rule catalogue + labels when label dialog opens ──
  useEffect(() => {
    if (!labelDialogOpen) return;
    (async () => {
      try {
        const [rulesResp, labelsResp] = await Promise.all([
          axios.get(`${API}/admin/clients/rules`),
          axios.get(`${API}/client-labels`),
        ]);
        setRules(rulesResp.data.rules || []);
        setLabels(labelsResp.data || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load rules / labels');
      }
    })();
  }, [labelDialogOpen]);

  // ── Bulk Set Gender handlers ──
  const openGenderDialog = async () => {
    setGenderDialogOpen(true);
    setGenderLoading(true);
    setGenderEdits({});
    try {
      const resp = await axios.get(`${API}/admin/clients/missing-gender`);
      setGenderClients(resp.data.clients || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load clients');
    } finally {
      setGenderLoading(false);
    }
  };

  const setEdit = (clientId, gender) => {
    setGenderEdits((prev) => {
      const next = { ...prev };
      if (!gender) {
        delete next[clientId];
      } else {
        next[clientId] = gender;
      }
      return next;
    });
  };

  const applyGenderEdits = async () => {
    const updates = Object.entries(genderEdits).map(([client_id, gender]) => ({
      client_id,
      gender,
    }));
    if (updates.length === 0) {
      toast.warning('No gender changes to save');
      return;
    }
    setGenderApplying(true);
    try {
      const resp = await axios.post(
        `${API}/admin/clients/bulk-set-gender`,
        { updates }
      );
      const { updated, skipped, rejected } = resp.data;
      toast.success(
        `Gender updates: ${updated} saved, ${skipped} unchanged, ${rejected} rejected`
      );
      // Remove the ones we just saved from the list
      const savedIds = new Set(
        resp.data.results
          .filter((r) => r.status === 'updated')
          .map((r) => r.client_id)
      );
      setGenderClients((prev) => prev.filter((c) => !savedIds.has(c.id)));
      setGenderEdits({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk set-gender failed');
    } finally {
      setGenderApplying(false);
    }
  };

  // ── Bulk Apply Label handlers ──
  const previewRule = async () => {
    if (!selectedRule) {
      toast.warning('Pick a rule first');
      return;
    }
    setPreviewLoading(true);
    setExcludedIds(new Set());
    try {
      const params = selectedLabel ? { label_id: selectedLabel } : {};
      const resp = await axios.get(
        `${API}/admin/clients/rule-preview/${selectedRule}`,
        { params }
      );
      setPreviewClients(resp.data.clients || []);
      setPreviewCount(resp.data.count || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleExclude = (id) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyLabel = async () => {
    if (!selectedRule || !selectedLabel) {
      toast.warning('Pick both a rule and a label');
      return;
    }
    const targets = previewClients
      .filter((c) => !excludedIds.has(c.id))
      .map((c) => c.id);
    if (targets.length === 0) {
      toast.warning('No clients selected');
      return;
    }
    setApplying(true);
    try {
      const resp = await axios.post(`${API}/admin/clients/bulk-apply-label`, {
        rule_id: selectedRule,
        label_id: selectedLabel,
        client_ids: targets,
      });
      const { applied, target_count } = resp.data;
      toast.success(
        `Label applied to ${applied} of ${target_count} client${target_count === 1 ? '' : 's'}`
      );
      // Reset preview — re-running the same rule should now return 0
      setPreviewClients([]);
      setPreviewCount(null);
      setExcludedIds(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk apply-label failed');
    } finally {
      setApplying(false);
    }
  };

  const filteredGenderClients = useMemo(() => {
    if (!genderSearch.trim()) return genderClients;
    const needle = genderSearch.trim().toLowerCase();
    return genderClients.filter((c) =>
      (c.display_name || '').toLowerCase().includes(needle)
    );
  }, [genderClients, genderSearch]);

  const editCount = Object.keys(genderEdits).length;
  const selectedRuleObj = rules.find((r) => r.id === selectedRule);
  const selectedLabelObj = labels.find((l) => l.id === selectedLabel);

  return (
    <>
      <Card className="border-blue-200" data-testid="bulk-client-ops-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UserCog className="h-5 w-5 text-blue-600" />
            Bulk Client Operations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-500">
            Repair common client-data gaps that cause eligibility 270 / claims
            998 rejections — without opening each chart one-by-one.
          </p>

          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={openGenderDialog}
            data-testid="bulk-set-gender-open-btn"
          >
            <Users className="h-4 w-4 mr-2" />
            Bulk Set Gender for Clients Missing It
          </Button>

          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => setLabelDialogOpen(true)}
            data-testid="bulk-apply-label-open-btn"
          >
            <Tag className="h-4 w-4 mr-2" />
            Bulk Apply Label by Rule
          </Button>
        </CardContent>
      </Card>

      {/* ── Bulk Set Gender Dialog ────────────────────────────────────── */}
      <Dialog open={genderDialogOpen} onOpenChange={setGenderDialogOpen}>
        <DialogContent
          className="max-w-3xl max-h-[80vh] flex flex-col"
          data-testid="bulk-set-gender-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Bulk Set Gender
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-3 mb-3">
            <Input
              placeholder="Search by name…"
              value={genderSearch}
              onChange={(e) => setGenderSearch(e.target.value)}
              className="flex-1"
              data-testid="bulk-set-gender-search"
            />
            <Badge variant="secondary" data-testid="bulk-set-gender-count">
              {filteredGenderClients.length} client
              {filteredGenderClients.length === 1 ? '' : 's'}
            </Badge>
            {editCount > 0 && (
              <Badge className="bg-blue-100 text-blue-800">
                {editCount} pending change{editCount === 1 ? '' : 's'}
              </Badge>
            )}
          </div>

          <div className="flex-1 overflow-auto border rounded">
            {genderLoading ? (
              <div className="p-8 text-center text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                Loading clients…
              </div>
            ) : filteredGenderClients.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No clients missing gender.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Name</th>
                    <th className="text-left p-2 font-medium">DOB</th>
                    <th className="text-left p-2 font-medium">Current</th>
                    <th className="text-left p-2 font-medium">Set To</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGenderClients.map((c) => (
                    <tr
                      key={c.id}
                      className="border-t hover:bg-gray-50"
                      data-testid={`bulk-set-gender-row-${c.id}`}
                    >
                      <td className="p-2">{c.display_name}</td>
                      <td className="p-2 text-gray-600">
                        {c.date_of_birth || '—'}
                      </td>
                      <td className="p-2 text-gray-500">
                        {c.gender || <span className="italic">blank</span>}
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          {['M', 'F'].map((g) => (
                            <Button
                              key={g}
                              size="sm"
                              variant={
                                genderEdits[c.id] === g ? 'default' : 'outline'
                              }
                              onClick={() =>
                                setEdit(
                                  c.id,
                                  genderEdits[c.id] === g ? null : g
                                )
                              }
                              className="h-7 w-9 p-0"
                              data-testid={`bulk-set-gender-${c.id}-${g}`}
                            >
                              {g}
                            </Button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter className="mt-3">
            <Button
              variant="outline"
              onClick={() => setGenderDialogOpen(false)}
              data-testid="bulk-set-gender-cancel-btn"
            >
              Close
            </Button>
            <Button
              onClick={applyGenderEdits}
              disabled={editCount === 0 || genderApplying}
              data-testid="bulk-set-gender-save-btn"
            >
              {genderApplying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving…
                </>
              ) : (
                `Save ${editCount} change${editCount === 1 ? '' : 's'}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Apply Label Dialog ───────────────────────────────────── */}
      <Dialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] flex flex-col"
          data-testid="bulk-apply-label-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Bulk Apply Label by Rule
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Find clients matching</Label>
              <Select value={selectedRule} onValueChange={setSelectedRule}>
                <SelectTrigger data-testid="bulk-label-rule-select">
                  <SelectValue placeholder="Pick a rule…" />
                </SelectTrigger>
                <SelectContent>
                  {rules.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Apply this label</Label>
              <Select value={selectedLabel} onValueChange={setSelectedLabel}>
                <SelectTrigger data-testid="bulk-label-label-select">
                  <SelectValue placeholder="Pick a label…" />
                </SelectTrigger>
                <SelectContent>
                  {labels.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-full"
                          style={{ backgroundColor: l.color || '#6366f1' }}
                        />
                        {l.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-3 my-3">
            <Button
              variant="outline"
              size="sm"
              onClick={previewRule}
              disabled={!selectedRule || previewLoading}
              data-testid="bulk-label-preview-btn"
            >
              {previewLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Previewing…
                </>
              ) : (
                'Preview matches'
              )}
            </Button>
            {previewCount !== null && (
              <Badge variant="secondary" data-testid="bulk-label-preview-count">
                {previewCount} match{previewCount === 1 ? '' : 'es'}
                {selectedLabel ? ' (excluding clients already labeled)' : ''}
              </Badge>
            )}
            {excludedIds.size > 0 && (
              <Badge className="bg-amber-100 text-amber-800">
                {excludedIds.size} unchecked
              </Badge>
            )}
          </div>

          <div className="flex-1 overflow-auto border rounded min-h-[200px]">
            {previewCount === null ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                Pick a rule + label, then click <strong>Preview matches</strong>.
              </div>
            ) : previewClients.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                No clients match this rule
                {selectedLabel ? ' and need this label' : ''}.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="text-left p-2 font-medium">Name</th>
                    <th className="text-left p-2 font-medium">DOB</th>
                    <th className="text-left p-2 font-medium">Gender</th>
                  </tr>
                </thead>
                <tbody>
                  {previewClients.map((c) => (
                    <tr
                      key={c.id}
                      className={`border-t hover:bg-gray-50 ${
                        excludedIds.has(c.id) ? 'opacity-50' : ''
                      }`}
                      data-testid={`bulk-label-row-${c.id}`}
                    >
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={!excludedIds.has(c.id)}
                          onChange={() => toggleExclude(c.id)}
                          data-testid={`bulk-label-checkbox-${c.id}`}
                        />
                      </td>
                      <td className="p-2">{c.display_name}</td>
                      <td className="p-2 text-gray-600">
                        {c.date_of_birth || '—'}
                      </td>
                      <td className="p-2 text-gray-500">{c.gender || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter className="mt-3">
            <Button
              variant="outline"
              onClick={() => setLabelDialogOpen(false)}
              data-testid="bulk-label-cancel-btn"
            >
              Close
            </Button>
            <Button
              onClick={applyLabel}
              disabled={
                !selectedRule ||
                !selectedLabel ||
                previewClients.length === 0 ||
                applying
              }
              data-testid="bulk-label-apply-btn"
            >
              {applying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Applying…
                </>
              ) : (
                `Apply "${selectedLabelObj?.name || 'label'}" to ${
                  previewClients.length - excludedIds.size
                } client${
                  previewClients.length - excludedIds.size === 1 ? '' : 's'
                }`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkClientOperationsCard;
